import { Navigate, RouteObject } from 'react-router-dom';
import UserFeaturesGuard from '~/guards/UserFeaturesGuard';
import UserRolesGuard from '~/guards/UserRolesGuard';
import { RouteConfig } from '~/routes/routeConfig';
import { useLoggedUser } from '~/services/user';
import { FeatureFlag, UserRole } from '~/types/company';
import lazyImport from '~/utils/lazyImport';

const TeamPage = lazyImport(() => import('~/views/settings/Team/Team'));
const UserManagementPage = lazyImport(() => import('~/views/settings/Team/UserManagementView/UserManagementView'));
const ApiKeyPage = lazyImport(() => import('~/views/settings/ApiKey/ApiKey'));
const FeqCredentialPage = lazyImport(() => import('~/views/settings/FeqCredential/FeqCredential'));
const PasskeyPage = lazyImport(() => import('~/views/settings/Passkey/Passkey'));
const MfaPage = lazyImport(() => import('~/views/settings/Mfa/Mfa'));

// The MFA settings section is only available to users who are required to use 2FA.
const MfaRouteGuard: React.FC = () => {
  const { data: loggedUser, isPending } = useLoggedUser();

  if (isPending) return null;
  if (!loggedUser?.mfaRequired) {
    return <Navigate to={RouteConfig.Settings.buildLinkWithChildPath('team')} replace />;
  }

  return <MfaPage />;
};

export const settingsRoutes: RouteObject[] = [
  {
    path: 'passkey',
    element: <PasskeyPage />,
  },
  {
    path: 'mfa',
    element: <MfaRouteGuard />,
  },
  {
    path: 'team/user/new',
    element: <UserManagementPage />,
  },
  {
    path: 'team/user/:companyUserId',
    element: <UserManagementPage />,
  },
  {
    path: 'team',
    element: <TeamPage />,
  },
  {
    path: 'api-key',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.IntegrationApi]}>
        <ApiKeyPage />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'feq-credential',
    element: (
      <UserRolesGuard
        allowedRoles={[UserRole.ENTREPRENEUR, UserRole.ADVISOR]}
        redirectTo={RouteConfig.SettingsTeam.buildLink()}
      >
        <FeqCredentialPage />
      </UserRolesGuard>
    ),
  },
  {
    path: '*',
    element: <Navigate to={RouteConfig.Settings.buildLinkWithChildPath('team')} replace />,
  },
];
